import { BoxProps, Grid, Heading, HStack, LinkBox, Text, VStack } from "@biblioteksentralen/react";
import { EditableDocument } from "@libry-content/common";
import { ImageWithMetadata } from "@libry-content/types";
import { ReactNode } from "react";
import { useSitePalette } from "../../utils/useSitePalette";
import { Edit } from "../editInSanity/EditInSanity";
import { SanityImageFillAvailableSpace } from "../images/SanityImageFillAvailableSpace";
import InternalLinkOverlay from "../InternalLinkOverlay";
import { ServiceImageContainer } from "./DigitalLibraryServicePreviewMobile";

type Props = {
  image?: ImageWithMetadata;
  imagePlaceholder?: ReactNode;
  heading?: string;
  headingSize?: "h2" | "h3";
  teaser?: string;
  href: string;
  doc: EditableDocument;
  tags?: ReactNode;
  isFullsize?: boolean;
} & BoxProps;

export const CommonServicePreview = ({
  image,
  imagePlaceholder,
  heading,
  teaser,
  href,
  doc,
  headingSize,
  tags,
  isFullsize,
  ...chakraProps
}: Props) => {
  const paletteColor = useSitePalette().colors.card;

  return (
    <LinkBox role="group" {...chakraProps}>
      <Grid
        border={`1px solid ${paletteColor.css.background}`}
        borderRadius="lg"
        padding="0"
        gridTemplateColumns="1fr 2fr"
        overflow="hidden"
        height="100%"
        minHeight="8rem"
      >
        {(image?.asset && (
          <SanityImageFillAvailableSpace
            image={image}
            resolution={500}
            borderRadius="none"
            hideCaption
            _groupHover={{ transform: "unset" }}
          />
        )) ||
          imagePlaceholder}

        <VStack
          alignItems="flex-start"
          justifyContent="center"
          padding={{ base: "1rem 1rem .5rem", md: isFullsize ? "2rem 1.5rem 2rem" : "1rem 1.5rem 1rem" }}
          marginInlineStart="0 !important"
          {...paletteColor.css}
        >
          <InternalLinkOverlay href={href} _groupHover={{ textDecoration: "underline" }}>
            <Heading as={headingSize || "h3"} size="md">
              <Text>{heading}</Text>
            </Heading>
          </InternalLinkOverlay>
          <Text
            size={{ base: "sm", md: isFullsize ? "md" : "sm" }}
            maxWidth={{ base: "100%", md: isFullsize ? "60%" : "100%" }}
          >
            {teaser}
          </Text>
          {tags}
          <Edit doc={doc} />
        </VStack>
      </Grid>
    </LinkBox>
  );
};

export const ServicePreviewMobile = ({
  image,
  imagePlaceholder,
  heading,
  teaser,
  href,
  doc,
  headingSize,
  tags,
  isFullsize,
  ...chakraProps
}: Props) => {
  const paletteColor = useSitePalette().colors.card;

  return (
    <LinkBox width="100%" role="group" padding="0.75rem" borderRadius="lg" {...chakraProps} {...paletteColor.css}>
      <HStack alignItems="flex-start" spacing="0.75rem">
        {!!image?.asset && (
          <ServiceImageContainer>
            <SanityImageFillAvailableSpace
              image={image}
              resolution={1080}
              hideCaption
              _groupHover={{ transform: "unset" }}
            />
          </ServiceImageContainer>
        )}
        <VStack alignItems="flex-start" spacing="0">
          <InternalLinkOverlay href={href} _groupHover={{ textDecoration: "underline" }}>
            <Heading as="h3" size="md">
              {heading}
            </Heading>
          </InternalLinkOverlay>
          <Text marginBottom="0.25rem">{teaser}</Text>
          {tags}
          <Edit doc={doc} />
        </VStack>
      </HStack>
    </LinkBox>
  );
};
